import { now } from "common";
import { compact, orderBy, uniq, uniqBy } from "lodash";
import { useMemo } from "react";
import {
    Audit,
    CatalogItemObject,
    Product,
    ProductObject,
    SelectionState,
    Seller,
    UserType,
    WareCatalogItem,
} from "types";
import {
    auditToLedgerRows,
    createdLedgerRow,
    inventoryAuditToLedgerRows,
    isCreationAudit,
    LedgerRow,
    NULL_VALUE,
} from "@/components/Reports/MerchandiseLedger/merchandiseLedgerHelper";
import { useUsers } from "@/hooks/resources/useUsers";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useResource } from "@/hooks/useResource";

export type ChangeType = "product" | "variant" | "inventory" | "all";
export const CHANGE_TYPES: ChangeType[] = ["product", "variant", "inventory", "all"];

// Variant Changes = catalog-item field edits (Event Category "Catalog"); Inventory Changes = lot stock
// movements (Purchase / Inventory Adjustment).
export const filterRowsByChangeType = (rows: LedgerRow[], changeType: ChangeType) => {
    if (changeType === "variant") {
        return rows.filter((row) => row.category === "Catalog");
    }

    if (changeType === "inventory") {
        // Whitelist the two categories so an unrecognized audit class (catch-all rows with no category)
        // is visibly excluded instead of landing in the Inventory tab.
        return rows.filter((row) => row.category === "Inventory Adjustment" || row.category === "Purchase");
    }

    return rows;
};

export const defaultRange = () => {
    return { from: now().subtract(7, "day").startOf("day"), to: now().endOf("day") };
};

// useSelection collapses "every product checked" into { all: true, selected: [] }, but this ledger keys
// off an explicit product-id list, so expand it back from storedIds.
export const expandAllSelection = (selection: SelectionState): SelectionState => {
    return selection.all ? { all: false, selected: selection.storedIds ?? [] } : selection;
};

// Signature matches ListFilter.fetch (seller, collection, params); only params are used.
export const useFetchWareProducts = (_seller: Seller, _collection: string[], params: Record<string, unknown>) => {
    return useAutoFetchCollection<Product>("products", { ...params, "object[in]": ProductObject.WARE });
};

// A ledger row's objectId can be the catalog item or one of its inventory lots, so both keys map to the
// same value.
export const buildCatalogItemMap = (
    items: WareCatalogItem[],
    valueFor: (item: WareCatalogItem) => string | undefined,
) => {
    const map: Record<string, string> = {};

    for (const item of items) {
        const value = valueFor(item);
        if (!value) {
            continue;
        }

        if (item.id) {
            map[item.id] = value;
        }

        for (const lot of item.inventory?.inventoryLots ?? []) {
            if (lot.id) {
                map[lot.id] = value;
            }
        }
    }

    return map;
};

const catalogItemParams = {
    "status[exists]": "true",
    expand: "inventory.inventoryLots",
    object: CatalogItemObject.WARE_CATALOG_ITEM,
};

// Product-level audits key off objectId/objectClass; inventory-level audits key off
// context.id/context.object.
const OBJECT_SCOPE = { idKey: "objectId", classKey: "objectClass" } as const;
const CONTEXT_SCOPE = { idKey: "context.id", classKey: "context.object" } as const;

const auditParams = (
    scope: { idKey: string; classKey: string },
    objectClass: string,
    id: string,
    sellerId: ID,
    rangeParam: string,
): Record<string, string> => {
    return { [scope.idKey]: id, [scope.classKey]: objectClass, seller: sellerId, "createdAt[range]": rangeParam };
};

const productParams = (productId: string, sellerId: ID, rangeParam: string) => {
    return auditParams(OBJECT_SCOPE, ProductObject.WARE, productId, sellerId, rangeParam);
};

const inventoryParams = (productId: string, sellerId: ID, rangeParam: string) => {
    return auditParams(CONTEXT_SCOPE, ProductObject.WARE, productId, sellerId, rangeParam);
};

// useInfiniteCollection stops following paging.next past this, so a product with a very long audit
// history can't spin forever.
const AUDIT_ITEM_LIMIT = 2000;

const useAudits = (
    productIds: string[],
    sellerId: ID,
    rangeParam: string,
    buildParams: (productId: string, sellerId: ID, rangeParam: string) => Record<string, string>,
    enabled: boolean,
) => {
    const isEnabled = enabled && productIds.length > 0;
    const paramsList = useMemo(() => {
        return productIds.map((productId) => buildParams(productId, sellerId, rangeParam));
    }, [productIds, sellerId, rangeParam, buildParams]);

    const {
        data: audits = [],
        isLoading,
        isError,
    } = useAutoFetchCollection<Audit>("audits", paramsList, {
        enabled: isEnabled,
        refetchOnMount: "always",
        queryItemLimit: AUDIT_ITEM_LIMIT,
    });

    return { audits, isLoading: isEnabled && isLoading, isError: isEnabled && isError };
};

// Two audit calls merged: the item's own audits (objectId) plus context-scoped audits (context.id) that
// carry its inventory-lot changes. Deduped by audit id; on date ties, item audits rank before lot audits.
export const useInventoryLedger = (
    catalogItemId: string | undefined,
    sellerId: ID,
    rangeParam: string,
    scoped: boolean,
) => {
    const itemId = catalogItemId ?? "";
    const wareItem = CatalogItemObject.WARE_CATALOG_ITEM;
    const enabled = Boolean(catalogItemId) && scoped;
    const itemAuditParams = auditParams(OBJECT_SCOPE, wareItem, itemId, sellerId, rangeParam);
    const contextAuditParams = auditParams(CONTEXT_SCOPE, wareItem, itemId, sellerId, rangeParam);

    // Expand the product so its name arrives on the same fetch, instead of a second sequential hop.
    const itemKey = ["catalogItems", itemId];
    const { data: catalogItem } = useResource<WareCatalogItem>(itemKey, { expand: "product" }, { enabled });

    const {
        data: itemAudits = [],
        isLoading: isItemLoading,
        isError: isItemError,
    } = useAutoFetchCollection<Audit>("audits", itemAuditParams, { refetchOnMount: "always", enabled });

    const {
        data: contextAudits = [],
        isLoading: isContextLoading,
        isError: isContextError,
    } = useAutoFetchCollection<Audit>("audits", contextAuditParams, { refetchOnMount: "always", enabled });

    const rows = useMemo(() => {
        const itemIds = new Set(itemAudits.map((audit) => audit.id));
        const decorated = uniqBy([...itemAudits, ...contextAudits], "id").flatMap((audit) => {
            const source = itemIds.has(audit.id) ? 0 : 1;
            return inventoryAuditToLedgerRows(audit).map((row) => ({ row, source }));
        });
        const sorted = orderBy(decorated, [({ row }) => row.date.valueOf(), ({ source }) => source], ["desc", "asc"]);

        return sorted.map((entry) => entry.row);
    }, [itemAudits, contextAudits]);

    const variantName = catalogItem?.name ?? "";
    const productName = catalogItem?.product?.name ?? "";

    // Every scoped row maps to the same name, so build over the unique objectIds. Fall back to the
    // placeholder, not "": the grid's nullish guard renders an empty string as a blank cell.
    const objectIds = useMemo(() => uniq(rows.map((row) => row.objectId)), [rows]);
    const variantNames = useMemo(() => {
        return Object.fromEntries(objectIds.map((id) => [id, variantName || NULL_VALUE]));
    }, [objectIds, variantName]);
    const productNames = useMemo(() => {
        return Object.fromEntries(objectIds.map((id) => [id, productName || NULL_VALUE]));
    }, [objectIds, productName]);

    return {
        rows,
        isLoading: isItemLoading || isContextLoading,
        isError: isItemError || isContextError,
        productId: catalogItem?.product?.id,
        variantName,
        productName,
        variantNames,
        productNames,
    };
};

// Product-level (ware) field edits show as "Product" in the Event Category column; creation docs surface
// a single "Created" row. Inventory rows keep the semantic category from the helper.
export const useMerchandiseLedger = (
    productIds: string[],
    sellerId: ID,
    rangeParam: string,
    changeType: ChangeType,
) => {
    const {
        audits: productAudits,
        isLoading: isProductLoading,
        isError: isProductError,
    } = useAudits(productIds, sellerId, rangeParam, productParams, changeType === "product" || changeType === "all");
    const {
        audits: inventoryAudits,
        isLoading: isInventoryLoading,
        isError: isInventoryError,
    } = useAudits(productIds, sellerId, rangeParam, inventoryParams, changeType !== "product");

    const rows = useMemo(() => {
        const productRows = productAudits
            .flatMap((audit) => (isCreationAudit(audit) ? [createdLedgerRow(audit)] : auditToLedgerRows(audit)))
            .map((row) => ({ ...row, category: "Product" as const }));

        // An audit matching both the product (objectId) and inventory (context.id) query would render
        // twice on the All tab; drop the inventory-side duplicate so the two sources stay symmetric.
        const productAuditIds = new Set(productAudits.map((audit) => audit.id));
        const inventoryRows = inventoryAudits
            .filter((audit) => !productAuditIds.has(audit.id))
            .flatMap((audit) => inventoryAuditToLedgerRows(audit));

        const combined =
            changeType === "product"
                ? productRows
                : changeType === "all"
                ? [...productRows, ...inventoryRows]
                : filterRowsByChangeType(inventoryRows, changeType);

        return orderBy(combined, (row) => row.date.valueOf(), "desc");
    }, [productAudits, inventoryAudits, changeType]);

    // Live plus deleted catalog items (a row can reference a variant/lot since deleted). Labels the
    // Variant column: catalog-item rows key off objectId, inventory-lot rows off the lot id.
    const productFilter = productIds.join(",");
    const liveItemParams = { ...catalogItemParams, "product[in]": productFilter };
    const deletedItemParams = { ...liveItemParams, "deletedAt[exists]": true };
    const isCatalogEnabled = productIds.length > 0 && changeType !== "product";
    const { data: catalogItems = [] } = useAutoFetchCollection<WareCatalogItem>(
        "catalogItems",
        [liveItemParams, deletedItemParams],
        { enabled: isCatalogEnabled },
    );

    const variantNames = useMemo(() => {
        return buildCatalogItemMap(catalogItems, (item) => item.name ?? item.sku);
    }, [catalogItems]);

    // Scoped to the selected ids so the filter tag labels don't pull the seller's whole ware catalog.
    const { data: wareProducts = [] } = useAutoFetchCollection<Product>(
        "products",
        { "object[in]": ProductObject.WARE, "id[in]": productIds.join(",") },
        { enabled: productIds.length > 0 },
    );
    const productNames = useMemo(() => {
        return Object.fromEntries(wareProducts.map((product) => [product.id, product.name]));
    }, [wareProducts]);

    // Product column resolves any row objectId -> ware name: product-audit rows carry the product id
    // directly; inventory rows carry a catalog-item / lot id, mapped to their product via product.id.
    const rowProductNames = useMemo(() => {
        const itemProductNames = buildCatalogItemMap(catalogItems, (item) => {
            return item.product?.id ? productNames[item.product.id] : undefined;
        });

        return { ...productNames, ...itemProductNames };
    }, [productNames, catalogItems]);

    return {
        rows,
        isLoading: isProductLoading || isInventoryLoading,
        isError: isProductError || isInventoryError,
        variantNames,
        productNames,
        rowProductNames,
    };
};

// useUsers fires one request per distinct actor; inventory rows carry a traveller per purchase, so a wide
// range can produce hundreds. Unresolved ids degrade to the raw id.
const USER_RESOLVE_LIMIT = 200;

// Traveller-type actors are public checkout users; everything else is office staff (seller/delegate).
export const useLedgerUsers = (rows: LedgerRow[]) => {
    const userIds = useMemo(() => uniq(compact(rows.map((row) => row.userId))).slice(0, USER_RESOLVE_LIMIT), [rows]);
    const users = useUsers(userIds);

    return useMemo(() => {
        const userNames: Record<string, string> = {};
        const userSources: Record<string, string> = {};
        for (const id of userIds) {
            const user = users[id];
            userNames[id] = user?.name ?? NULL_VALUE;
            userSources[id] = user ? (user.type === UserType.TRAVELER ? "Checkout" : "Office") : NULL_VALUE;
        }
        return { userNames, userSources };
    }, [userIds, users]);
};
